import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

let currentlyPlayingAudio = null;
let audioElements = [];

export function setupWaveform(container, src, options = {}) {
  const { height = 25, autonext = false } = options;

  // Create a new root element
  const root = document.createElement("div");
  // NOTE: Complex cssText replaced with utility classes for CSP compliance
  root.className = "custom-waveform-player pos-relative w-100 h-100 is-flex is-align-items-center";

  // If a valid container is provided, append the root to it
  // Otherwise, append to the document body
  if (container && container instanceof HTMLElement) {
    container.appendChild(root);
  } else {
    document.body.appendChild(root);
  }

  // Create waveform container
  const waveContainer = document.createElement("div");
  waveContainer.id = "waveform";
  // NOTE: Complex cssText replaced with utility classes for CSP compliance
  waveContainer.className = "flex-grow-1 h-100";
  root.insertBefore(waveContainer, root.firstChild); // Insert button before waveContainer

  // Update play/pause button style
  const playPauseBtn = document.createElement("button");
  // NOTE: Complex cssText replaced with utility classes for CSP compliance
  playPauseBtn.className = "play-pause-btn w-25px h-25px border-none border-radius-50 cursor-pointer is-flex is-justify-content-center is-align-items-center op-0-7 mr-10px";
  // Background, color, box-shadow, z-index, and transition use CSS variables/classes
  playPauseBtn.innerHTML =
    '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M8 5v14l11-7z" fill="var(--text-secondary)"/></svg>';
  root.insertBefore(playPauseBtn, waveContainer); // Insert button before waveContainer

  // Update hover effects
  playPauseBtn.addEventListener("mouseenter", () => {
    _$(playPauseBtn).css("opacity", "1");
  });
  playPauseBtn.addEventListener("mouseleave", () => {
    _$(playPauseBtn).css("opacity", "0.7");
  });

  const audio = new Audio(src);

  // Function to get computed CSS variable value
  function getCSSVariableValue(variableName) {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();
  }

  // Get color values
  const progressColor = getCSSVariableValue("--accent-color");
  const waveColor = getCSSVariableValue("--border-color");
  const cursorColor = getCSSVariableValue("--border-color");

  const waveSurfer = WaveSurfer.create({
    container: waveContainer,
    barWidth: 2,
    barGap: 1,
    height: height, // Use the height from options or default to 25
    barRadius: 3,
    progressColor: progressColor,
    waveColor: waveColor,
    cursorColor: cursorColor,
    backend: "MediaElement",
    mediaControls: false,
    media: audio,
    hideScrollbar: true,
    responsive: true,
    normalize: true, // Add this to ensure the waveform fits the container
  });

  waveSurfer.load(src);

  // Add this audio element to the list
  audioElements.push({ type: "wavesurfer", instance: waveSurfer, autonext });

  playPauseBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    togglePlayPause(waveSurfer);
  });

  // Add this new event listener for waveform clicks
  waveContainer.addEventListener("click", (e) => {
    e.stopPropagation();
  });

  // Update play/pause button on playback events
  waveSurfer.on("play", () => {
    if (currentlyPlayingAudio && currentlyPlayingAudio !== waveSurfer) {
      currentlyPlayingAudio.pause();
    }
    currentlyPlayingAudio = waveSurfer;
    playPauseBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" fill="var(--text-secondary)"/></svg>';
  });

  waveSurfer.on("pause", () => {
    playPauseBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M8 5v14l11-7z" fill="var(--text-secondary)"/></svg>';
  });

  // Add ended event listener
  waveSurfer.on("finish", () => handleAudioEnded(waveSurfer));

  // Add new event listener for seek
  waveSurfer.on("seek", function (progress) {
    if (!waveSurfer.isPlaying()) {
      waveSurfer.play();
    }
  });

  return { waveSurfer };
}

export function setupCircularPlayer(root, src, options = {}) {
  const {
    strokeWidth = 10,
    strokeColor = "var(--bg-primary-progress)",
    backgroundColor = "var(--accent-color)",
    playIconPath = "M8 5v14l11-7z",
    autonext = false,
    width = options.width || 30,
    height = options.height || 30,
  } = options;

  const audio = document.createElement("audio");
  audio.src = src;
  _$(audio).hide(); // Hide the audio element
  const playerContainer = document.createElement("div");
  playerContainer.className = "circular-player";
  // Use CSSOM to set styles (CSP-compliant)
  playerContainer.style.setProperty("position", "relative");
  playerContainer.style.setProperty("cursor", "pointer");
  playerContainer.style.setProperty("width", "100%");
  playerContainer.style.setProperty("height", "100%");
  playerContainer.style.setProperty("display", "flex");
  playerContainer.style.setProperty("justify-content", "center");
  playerContainer.style.setProperty("align-items", "center");
  root.appendChild(playerContainer);
  playerContainer.appendChild(audio);

  // Create SVG for progress ring
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "progress-ring");
  svg.setAttribute("width", "100%");
  svg.setAttribute("height", "100%");
  svg.setAttribute("viewBox", "0 0 100 100");
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  // Use CSSOM to set styles (CSP-compliant)
  svg.style.setProperty("position", "absolute");
  svg.style.setProperty("top", "0");
  svg.style.setProperty("left", "0");
  svg.style.setProperty("pointer-events", "none");

  // Calculate initial container size for setting up circles
  const initialContainerSize = Math.min(width, height);
  const initialPixelRadius = initialContainerSize / 2 - strokeWidth / 2;
  const initialViewBoxRadius = (initialPixelRadius / initialContainerSize) * 100;
  const initialViewBoxBackgroundRadius = ((initialPixelRadius + strokeWidth / 2) / initialContainerSize) * 100;
  const initialViewBoxStrokeWidth = (strokeWidth / initialContainerSize) * 100;

  // Create background circle
  const backgroundCircle = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "circle"
  );
  backgroundCircle.setAttribute("fill", backgroundColor);
  backgroundCircle.setAttribute("cx", "50");
  backgroundCircle.setAttribute("cy", "50");
  backgroundCircle.setAttribute("r", initialViewBoxBackgroundRadius);

  // Create border circle
  const borderCircle = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "circle"
  );
  borderCircle.setAttribute("stroke", strokeColor);
  borderCircle.setAttribute("stroke-width", initialViewBoxStrokeWidth);
  borderCircle.setAttribute("fill", "none");
  borderCircle.setAttribute("cx", "50");
  borderCircle.setAttribute("cy", "50");
  borderCircle.setAttribute("r", initialViewBoxRadius);

  svg.appendChild(backgroundCircle);
  svg.appendChild(borderCircle);
  playerContainer.appendChild(svg);

  // Create play/pause button
  const playPauseBtn = document.createElement("button");
  playPauseBtn.className = "play-pause-btn";
  // Use CSSOM to set styles (CSP-compliant)
  playPauseBtn.style.setProperty("background", "none");
  playPauseBtn.style.setProperty("border", "none");
  playPauseBtn.style.setProperty("cursor", "pointer");
  playPauseBtn.style.setProperty("outline", "none");
  playPauseBtn.style.setProperty("width", "100%");
  playPauseBtn.style.setProperty("height", "100%");
  playPauseBtn.style.setProperty("display", "flex");
  playPauseBtn.style.setProperty("justify-content", "center");
  playPauseBtn.style.setProperty("align-items", "center");
  playPauseBtn.style.setProperty("z-index", "1");
  playPauseBtn.style.setProperty("padding", "0");
  // Color uses CSS variable
  playPauseBtn.innerHTML = `
    <svg viewBox="0 0 24 24" width="50%" height="50%">
      <path d="${playIconPath}" fill="var(--text-secondary)"/>
    </svg>
  `;
  playerContainer.appendChild(playPauseBtn);

  function setProgress(percent) {
    const offset = circumference - (percent / 100) * circumference;
    // Use setAttribute for SVG properties (CSP-compliant, no inline style)
    borderCircle.setAttribute("stroke-dashoffset", offset);
  }

  function seek(e) {
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const angle = Math.atan2(y, x);
    let percent = (angle / (2 * Math.PI)) * 100;
    percent = (percent + 25) % 100; // Adjust to start from the top
    const seekTime = (percent / 100) * audio.duration;
    audio.currentTime = seekTime;
    setProgress(percent);
  }

  svg.addEventListener("click", seek);

  playPauseBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    togglePlayPause(audio);
  });

  audio.addEventListener("play", () => {
    if (currentlyPlayingAudio && currentlyPlayingAudio !== audio) {
      currentlyPlayingAudio.pause();
    }
    currentlyPlayingAudio = audio;
    updatePlayPauseIcon(playPauseBtn, true);
  });

  audio.addEventListener("pause", () => {
    updatePlayPauseIcon(playPauseBtn, false);
  });

  audio.addEventListener("timeupdate", () => {
    const percent = (audio.currentTime / audio.duration) * 100;
    setProgress(percent);
  });

  // Make the player responsive
  function drawProgressbar() {
    const containerSize = Math.min(width, height);
    // Round dynamic values to nearest 10px for utility class compatibility
    const roundedSize = Math.round(containerSize / 10) * 10;
    _$(playerContainer).css("width", `${roundedSize}px`);
    _$(playerContainer).css("height", `${roundedSize}px`);

    // Calculate radius in pixels
    const pixelRadius = containerSize / 2 - strokeWidth / 2;
    // Convert pixel radius to viewBox coordinates (viewBox is 100x100, so center is 50,50)
    // The viewBox represents the full container, so radius in viewBox = (pixelRadius / containerSize) * 100
    const viewBoxRadius = (pixelRadius / containerSize) * 100;
    const viewBoxBackgroundRadius = ((pixelRadius + strokeWidth / 2) / containerSize) * 100;
    const viewBoxStrokeWidth = (strokeWidth / containerSize) * 100;
    
    backgroundCircle.setAttribute("r", viewBoxBackgroundRadius);
    borderCircle.setAttribute("r", viewBoxRadius);
    borderCircle.setAttribute("stroke-width", viewBoxStrokeWidth);

    // Recalculate circumference using viewBox radius (for stroke-dasharray)
    // Since viewBox is 100x100, circumference = 2 * PI * viewBoxRadius
    circumference = 2 * Math.PI * viewBoxRadius;
    // Use setAttribute for SVG properties (CSP-compliant, no inline style)
    borderCircle.setAttribute("stroke-dasharray", `${circumference} ${circumference}`);
    borderCircle.setAttribute("stroke-dashoffset", circumference);
  }

  // Initial resize
  let circumference;
  drawProgressbar();

  // Add this audio element to the list
  audioElements.push({ type: "audio", instance: audio, autonext });

  // Add ended event listener
  audio.addEventListener("ended", () => handleAudioEnded(audio));

  return { audio };
}

// Add this helper function
function togglePlayPause(audioInstance) {
  if (audioInstance instanceof WaveSurfer) {
    if (audioInstance.isPlaying()) {
      audioInstance.pause();
    } else {
      if (currentlyPlayingAudio && currentlyPlayingAudio !== audioInstance) {
        currentlyPlayingAudio.pause();
      }
      audioInstance.play();
    }
  } else if (audioInstance instanceof HTMLAudioElement) {
    if (audioInstance.paused) {
      if (currentlyPlayingAudio && currentlyPlayingAudio !== audioInstance) {
        currentlyPlayingAudio.pause();
      }
      audioInstance.play();
    } else {
      audioInstance.pause();
    }
  }
}

// Add this helper function
function updatePlayPauseIcon(button, isPlaying) {
  const playIconPath = "M8 5v14l11-7z";
  const pauseIconPath = "M6 19h4V5H6v14zm8-14v14h4V5h-4z";

  button.innerHTML = `
    <svg viewBox="0 0 24 24" width="50%" height="50%">
      <path d="${
        isPlaying ? pauseIconPath : playIconPath
      }" fill="var(--text-secondary)"/>
    </svg>
  `;
}

// Add this new function to handle audio ending
function handleAudioEnded(audioInstance) {
  // Reset progress for circular player with transition
  if (audioInstance instanceof HTMLAudioElement) {
    const playerContainer = audioInstance.closest(".circular-player");
    if (playerContainer) {
      const borderCircle = playerContainer.querySelector("circle[stroke]");
      if (borderCircle) {
        const radius = parseFloat(borderCircle.getAttribute("r"));
        const circumference = 2 * Math.PI * radius;

        // Add transition via CSS class (CSP-compliant)
        borderCircle.classList.add("progress-transition");

        // Set the final state after a small delay to ensure the transition is applied
        setTimeout(() => {
          borderCircle.setAttribute("stroke-dasharray", `${circumference} ${circumference}`);
          borderCircle.setAttribute("stroke-dashoffset", circumference);
        }, 10);

        // Remove the transition after it's complete
        setTimeout(() => {
          borderCircle.classList.remove("progress-transition");
        }, 1010);
      }
    }
  }

  const currentIndex = audioElements.findIndex(
    (el) => el.instance === audioInstance
  );
  if (currentIndex === -1 || !audioElements[currentIndex].autonext) return;

  const nextIndex = currentIndex + 1;

  // Check if there is a next element
  if (nextIndex < audioElements.length) {
    const nextAudio = audioElements[nextIndex];
    if (nextAudio.type === "wavesurfer") {
      nextAudio.instance.play();
    } else if (nextAudio.type === "audio") {
      nextAudio.instance.play();
    }
  } else {
    // If it's the last element, do nothing (it will stop playing)
    console.log("Reached the end of the playlist");
  }
}
